import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Typography, Paper, CircularProgress, Divider } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import { RMSButton } from '../../shared/components/buttons';
import { executeApiAction } from '../../shared/utility/apiHelper';
import { buildShareBlobUrl } from '../constants/shareLinkConsts';
import { getFileBadge, formatFileSize } from '../constants/attachmentConsts';

const PageBundleShareView = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const apiUrl = window.__RUNTIME_CONFIG__.REACT_APP_API_URL;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [fileActionId, setFileActionId] = useState(null);
  const [zipping, setZipping] = useState(false);

  const fetchBundle = useCallback(
    async (queryString = '') => {
      const response = await fetch(
        `${apiUrl}/public-attachment-share-bundle/${token}${queryString}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load this bundle.');
      }

      return data;
    },
    [apiUrl, token]
  );

  useEffect(() => {
    executeApiAction({
      setLoading,
      dispatch,
      action: () => fetchBundle(),
      onSuccess: setBundle,
      onError: (err) => setError(err.message),
    });
  }, [fetchBundle, dispatch]);

  const triggerDownload = (data) => {
    const url = buildShareBlobUrl(data);
    const link = document.createElement('a');

    link.href = url;
    link.download = data.fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handleView = async (file) => {
    const previewWindow = window.open('', '_blank');

    setFileActionId(file.ptsAttachmentID);

    await executeApiAction({
      dispatch,
      action: () => fetchBundle(`?attachmentID=${file.ptsAttachmentID}`),
      onSuccess: (data) => {
        const url = buildShareBlobUrl(data);

        if (previewWindow) previewWindow.location.href = url;
      },
      onError: (err) => {
        setError(err.message);
        previewWindow?.close();
      },
      onFinally: () => setFileActionId(null),
    });
  };

  const handleDownloadFile = async (file) => {
    setFileActionId(file.ptsAttachmentID);

    await executeApiAction({
      dispatch,
      action: () => fetchBundle(`?attachmentID=${file.ptsAttachmentID}&download=1`),
      onSuccess: triggerDownload,
      onError: (err) => setError(err.message),
      onFinally: () => setFileActionId(null),
    });
  };

  const handleDownloadZip = async () => {
    await executeApiAction({
      setLoading: setZipping,
      dispatch,
      action: () => fetchBundle('?zip=1'),
      onSuccess: triggerDownload,
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Box className="public-share-page">
      <Paper elevation={0} className="public-share-card public-bundle-card">
        <Box className="public-share-header">
          <LockIcon className="public-share-header-icon" />
          <Typography variant="h6">PTS Evidence Management — Shared Files</Typography>
        </Box>

        <Box className="public-share-body">
          {loading && (
            <Box className="public-share-loading">
              <CircularProgress size={28} />
              <Typography variant="body2">Loading shared files…</Typography>
            </Box>
          )}

          {!loading && error && (
            <Box className="public-share-error">
              <Typography variant="subtitle1">This link can&apos;t be opened</Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {!loading && !error && bundle && (
            <>
              <Typography variant="subtitle1" className="public-share-filename">
                {bundle.bundleName}
              </Typography>

              <Typography variant="caption" className="public-share-expiry">
                {bundle.files.length} file{bundle.files.length === 1 ? '' : 's'} · Link expires{' '}
                {new Date(bundle.expiresAt).toLocaleString()}
              </Typography>

              {bundle.allowDownload && (
                <Box className="public-bundle-zip-row">
                  <RMSButton
                    label={zipping ? 'Preparing ZIP…' : 'Download All as ZIP'}
                    onClick={handleDownloadZip}
                    disabled={zipping}
                  />
                </Box>
              )}

              <Divider className="public-bundle-divider" />

              <Box className="public-bundle-file-list">
                {bundle.files.map((file) => (
                  <Box key={file.ptsAttachmentID} className="public-bundle-file-row">
                    <span className="public-bundle-file-badge">{getFileBadge(file.fileType)}</span>

                    <Box className="public-bundle-file-info">
                      <Typography variant="body2" className="public-bundle-file-name" noWrap>
                        {file.fileName}
                      </Typography>
                      <Typography variant="caption" className="public-bundle-file-size">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>

                    <Box className="public-bundle-file-actions">
                      <RMSButton
                        label="View"
                        size="small"
                        variant="outlined"
                        disabled={fileActionId === file.ptsAttachmentID}
                        onClick={() => handleView(file)}
                      />

                      {bundle.allowDownload && (
                        <RMSButton
                          label="Download"
                          size="small"
                          variant="outlined"
                          disabled={fileActionId === file.ptsAttachmentID}
                          onClick={() => handleDownloadFile(file)}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PageBundleShareView;
